import {
  afterNextRender,
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  Injector,
  input,
  model,
  signal,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import type { FormValueControl } from '@angular/forms/signals';
import { UiFieldControl } from '../../../basics/ui-field-control';
import { InputFieldComponent } from '../../atoms/input-field/input-field.component';
import { IconUIComponent } from '../../atoms/icon/icon.component';
import { UiFieldActionDirective } from '../../../directives';
import { InputSizeType } from '../../../types';

@Component({
  selector: 'fr8bot-ui-input-search-field',
  imports: [InputFieldComponent, IconUIComponent, UiFieldActionDirective, TranslatePipe],
  templateUrl: './input-search-field.component.html',
  styleUrl: './input-search-field.component.scss',
  host: {
    '[class.search-input--collapsible]': 'collapsible()',
    '[class.search-input--collapsed]': 'collapsed()',
    '[class.search-input--small]': "size() === 'small'",
    '[class.search-input--animated]': 'animated()',
    '(pointerenter)': 'animated.set(true)',
    '(focusin)': 'animated.set(true)',
    '(focusout)': 'onFocusOut($event)',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputSearchFieldComponent
  extends UiFieldControl
  implements FormValueControl<string | null>
{
  readonly value = model<string | null>(null);

  readonly label = input<string | null>(null);
  readonly placeholder = input('');
  readonly size = input<InputSizeType>('large');
  /** Starts as a search button and folds back into it whenever focus leaves an empty field. */
  readonly collapsible = input(false, { transform: booleanAttribute });

  private readonly expanded = signal(false);
  // Transitions stay off until the user reaches the field: otherwise the first style pass
  // (component styles can land after the host renders) plays the fold on page open.
  protected readonly animated = signal(false);
  // A query keeps the field open, including one set from outside while it was folded.
  protected readonly collapsed = computed(
    () => this.collapsible() && !this.expanded() && !this.value(),
  );
  // The `[search-postfix]` slot replaces the magnifier; it is detected once, not tracked.
  protected readonly hasOuterPostfix = signal(false);

  private readonly element: HTMLElement = inject(ElementRef).nativeElement;
  private readonly injector = inject(Injector);

  constructor() {
    super();

    afterNextRender(() =>
      this.hasOuterPostfix.set(!!this.element.querySelector('[search-postfix]')),
    );
  }

  // Folded, the magnifier is the toggle; open, it only points the caret at the input.
  protected onMagnifierClick(): void {
    if (!this.collapsed()) {
      this.focusInput();
      return;
    }
    this.expanded.set(true);
    afterNextRender(() => this.focusInput(), { injector: this.injector });
  }

  protected focusInput(): void {
    this.element.querySelector('input')?.focus();
  }

  protected clearInput(): void {
    this.expanded.set(true);
    this.value.set(null);
    this.focusInput();
  }

  protected onFocusOut(event: FocusEvent): void {
    // Moving to the clear button or the magnifier is still inside the field.
    if (this.element.contains(event.relatedTarget as Node | null)) return;
    if (!this.value()) this.expanded.set(false);
  }
}
